import { Search, User as UserIcon, Moon, Sun, ShoppingBag } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useTheme } from "next-themes";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import { useIsMobile } from "@/hooks/use-mobile";
import { NotificationsPopover } from "./notifications/NotificationsPopover";
import { CartDrawer } from "./cart/CartDrawer";
import { useUser } from "@/hooks/use-user";

export const Navigation = () => {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { user, isLoading } = useUser();
  const [userProfile, setUserProfile] = useState<any>(null);
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setUserProfile(user);
    } else {
      setUserProfile(null);
    }
  }, [user]);

  // Ensure the session is still valid and refresh if needed
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session && session.expires_at) {
        // If session exists but is close to expiring, refresh it
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        const timeToExpire = expiresAt.getTime() - now.getTime();
        
        // If session expires in less than 15 minutes, refresh it
        if (timeToExpire < 15 * 60 * 1000) {
          console.log("Session close to expiry, refreshing...");
          await supabase.auth.refreshSession();
        }
      }
    };

    checkSession();
    
    // Set up a refresh interval to ensure token stays fresh
    const refreshInterval = setInterval(checkSession, 10 * 60 * 1000); // Check every 10 minutes
    
    return () => clearInterval(refreshInterval);
  }, []);

  const handleUserClick = () => {
    if (user) {
      navigate(`/profile/${user.id}`);
    } else {
      navigate('/auth');
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 w-full bg-background/80 backdrop-blur-lg border-b border-border z-50 transition-colors duration-300">
      <div className="w-full max-w-[1920px] mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img 
            src="/lovable-uploads/725d4a82-6ca0-4e39-8b72-3e42aff89638.png" 
            alt="Logo" 
            className="h-10 w-10 rounded-full cursor-pointer" 
            onClick={() => navigate('/')}
          />
          <span 
            className="font-bold text-lg text-foreground hidden sm:inline cursor-pointer" 
            onClick={() => navigate('/')}
          >
            Ink Webtoons
          </span>
        </div>
        
        {!isMobile && (
          <div className="flex-1 max-w-xl px-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground/60" />
              <Input
                placeholder="Pesquisar..."
                className="w-full bg-muted/30 border-muted pl-10 text-foreground/80 placeholder:text-foreground/60 rounded-full h-9"
              />
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-3 sm:gap-4">
          {mounted && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-foreground/80 hover:bg-background/10 rounded-full"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
          )}
          
          {!isMobile && (
            <>
              <NotificationsPopover />
              
              <CartDrawer />

              <Button
                variant="ghost"
                size="icon"
                className="text-foreground/80 hover:bg-background/10 rounded-full overflow-hidden"
                onClick={handleUserClick}
              >
                {userProfile?.avatar_url ? (
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={userProfile.avatar_url} alt="User profile" />
                    <AvatarFallback>{userProfile.username?.charAt(0)?.toUpperCase() || "U"}</AvatarFallback>
                  </Avatar>
                ) : (
                  <UserIcon className="h-5 w-5" />
                )}
              </Button>
            </>
          )}
          
          <Button 
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-sm px-6"
            onClick={() => navigate('/create')}
          >
            Criar InkToons
          </Button>
        </div>
      </div>
    </nav>
  );
}
